import { Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import firebase from 'firebase/app';
import 'firebase/auth';
import { NgForm } from '@angular/forms';
import { touchForm } from '../../utils/touch-form';
import { Router } from '@angular/router';
import { ToastService } from '../../services/toast.service';
import { RaygunService } from '../../services/raygun.service';
import { MfaChallenge } from '../../services/mfa-challenge';

@Component({
    selector: 'app-signup',
    templateUrl: './signup.component.html',
    styleUrls: ['./signup.component.scss'],
    standalone: false
})
export class SignupComponent implements OnInit, OnDestroy {

  /**
   * Host for the invisible reCAPTCHA widget. Kept outside the form so it exists from
   * initialisation, whatever the form is showing.
   */
  @ViewChild('recaptchaHost', { static: true })
  public recaptchaHost: ElementRef<HTMLElement>;

  @Input()
  public token: string;

  @Input()
  public user: string;

  public password: string;

  public passwordRepeat: string;

  public error: string;

  public verifying = true;

  public expired: boolean = null; // expired unless proven

  public requestInProgress = false;

  /**
   * The SMS challenge Firebase demands before an account that already has a second factor
   * can complete a sign-in - which this flow needs before it may set a password.
   */
  public mfa: MfaChallenge;

  private mfaResolver: any;

  constructor(private authService: AuthService,
              private router: Router,
              private toastService: ToastService,
              private raygun: RaygunService,
              private ngZone: NgZone) {
    this.mfa = new MfaChallenge(this.authService, this.ngZone, () => this.recaptchaHost.nativeElement);
  }

  public ngOnInit(): void {
    this.authService
      .loginToVerify(this.user, this.token)
      .toPromise()
      .then(() => {
        this.expired = false;
      })
      .catch((e) => {
        // An account that already has a second factor cannot finish a password-only
        // sign-in - but the credentials themselves were accepted, so the link is valid.
        // Treating this as expired locked such users out of their own invite.
        if (this.authService.isMfaRequired(e)) {
          this.expired = false;
          return;
        }
        // The second rejection that does not mean a bad link, for the same reason: the
        // throttle is a property of the attempts, not of the invite (Bug 47869). Sending a
        // throttled user to ask for a replacement link gets them one that is refused
        // identically - and this is the page they land on after reloading, which is the
        // only way out of a form the throttle has disabled.
        if (e?.code === 'auth/too-many-requests') {
          this.expired = false;
          this.mfa.noteThrottled(e);
          // Reloading is not the advice here that it is on the login form: this check runs
          // from ngOnInit, so a reload spends another attempt before the user has done
          // anything. Coming back to the link later is both the remedy and the way back.
          this.showSignInError(e, 'Could not verify your link.');
          return;
        }
        this.expired = true;
        this.router.navigate(['public/invalid-link']);
      })
      .finally(() => {
        this.verifying = false;
      });
  }

  public ngOnDestroy(): void {
    this.resetChallenge();
  }

  /**
   * Step one: sign in with the invite token, then set the chosen password. An account with
   * a second factor gets an SMS challenge here instead of an error.
   */
  public async update(form: NgForm): Promise<void> {
    if (this.requestInProgress) {
      return;
    }
    this.error = null;
    touchForm(form);

    if (form.invalid) {
      return;
    }

    this.requestInProgress = true;
    // Captured before the sign-in round-trip, so a challenge is not started for a form the
    // user has since navigated away from.
    const epoch = this.mfa.epoch;
    let credential: firebase.auth.UserCredential;
    try {
      credential = await this.authService
        .login(this.user, this.token)
        .toPromise();
    } catch (e) {
      if (this.authService.isMfaRequired(e)) {
        await this.startMfaChallenge(e.resolver, epoch);
      } else {
        // A throttle on the sign-in above never reaches the challenge, so hand it over -
        // otherwise Update stays enabled and each click extends the block (Bug 47869). The
        // message comes from the sign-in mapper for the same reason ngOnInit's does: this
        // is the token exchange failing, before any second factor is involved.
        //
        // The fallback is mapMfaError's own mapping rather than the raw string: the sign-in
        // mapper only overrides the throttle case and returns its fallback untouched for
        // every other code, so running the fallback through mapMfaError first composes the
        // two without a third mapper - a dropped connection still reads as a network error
        // rather than an expired link, and an unmapped code still gets the code appended.
        this.mfa.noteThrottled(e, epoch);
        this.showSignInError(
          e, this.authService.mapMfaError(e, 'Password update failed. Your link may have expired.'), epoch);
      }
      this.ngZone.run(() => {
        this.requestInProgress = false;
      });
      return;
    }

    await this.applyPassword(credential);
  }

  /**
   * Step two (accounts with a second factor only): the SMS code completes the sign-in,
   * after which the password can be set.
   */
  public async verifyMfaCodeAndUpdate(): Promise<void> {
    if (this.requestInProgress) {
      return;
    }
    this.error = null;
    this.requestInProgress = true;
    let credential: firebase.auth.UserCredential;
    try {
      credential = await this.authService.resolveMfaSignIn(this.mfaResolver, this.mfa.id, this.mfa.code);
    } catch (e) {
      this.showError(e, 'Could not verify the code. Please try again or request a new one.');
      this.ngZone.run(() => {
        this.requestInProgress = false;
      });
      return;
    }

    // The resolver and verification id are single-use: once the code above is accepted, a
    // failure in applyPassword cannot be retried on this challenge.
    await this.applyPassword(credential, true);
  }

  /** Sends a replacement code, retiring the previous verification id first. */
  public async resendMfaCode(): Promise<void> {
    if (this.requestInProgress || !this.mfa.canResend) {
      return;
    }
    this.requestInProgress = true;
    this.error = null;
    try {
      if (await this.mfa.resend()) {
        this.ngZone.run(() => this.toastService.addSuccess('New verification code sent'));
      }
    } catch (e) {
      this.showError(e, 'Could not send a new verification code. Please try again.');
    } finally {
      this.ngZone.run(() => {
        this.requestInProgress = false;
      });
    }
  }

  private async startMfaChallenge(resolver: any, epoch: number): Promise<void> {
    try {
      const sent = await this.mfa.send(
        (verifier) => this.authService.sendMfaChallengeCode(resolver, verifier),
        { epoch, phoneHint: this.authService.phoneHint(resolver) });
      if (sent) {
        this.mfaResolver = resolver;
      }
    } catch (e) {
      this.showError(e, 'Could not send a verification code. Please try again.', epoch);
    }
  }

  /**
   * Sets the password on the freshly signed-in account. Reached by both the plain and the
   * multi-factor route, so the failure handling lives here once.
   */
  private async applyPassword(credential: firebase.auth.UserCredential, mfaCodeAccepted = false): Promise<void> {
    try {
      await credential.user.updatePassword(this.password);
    } catch (e) {
      this.ngZone.run(() => {
        this.error = 'Password update failed. Please try again.';
        this.requestInProgress = false;
        if (mfaCodeAccepted) {
          // The code was consumed but the password update failed - drop back to the
          // password fields; the next attempt re-authenticates and sends a fresh code.
          this.resetChallenge();
        }
      });
      // Validators should have caught anything wrong with the password, so this is worth
      // investigating.
      this.raygun.send(e, { source: 'Signup password update.' });
      // Log out again if the operation errors out.
      this.authService.logout().subscribe();
      return;
    }

    this.reloadPage();
  }

  private resetChallenge(): void {
    this.mfaResolver = null;
    this.mfa.reset();
  }

  /** Seam so tests can assert the reload without restarting the test runner. */
  private reloadPage(): void {
    window.location.reload();
  }

  /**
   * Firebase resolves its promises outside the Angular zone, so a message set after an
   * await has to re-enter the zone or the view will not show it.
   */
  private showError(e: any, fallback: string, epoch?: number): void {
    this.ngZone.run(() => {
      if (epoch !== undefined && !this.mfa.isCurrent(epoch)) {
        // The challenge was abandoned while this was in flight - the message would land on
        // a form nobody is looking at.
        return;
      }
      this.error = this.authService.mapMfaError(e, fallback);
    });
  }

  /**
   * As showError, for a rejection from the invite link's token exchange rather than from
   * the second-factor challenge that may follow it. Same epoch guard; different mapper.
   */
  private showSignInError(e: any, fallback: string, epoch?: number): void {
    this.ngZone.run(() => {
      if (epoch !== undefined && !this.mfa.isCurrent(epoch)) {
        return;
      }
      this.error = this.authService.mapSignInError(
        e, fallback, 'Open your invite link again later to try again.');
    });
  }
}
